import React from 'react'
import type { SongSettings, SectionStructure, SectionType } from '../../../../types/song'
import { STRUCTURAL_BOX_OPTIONS, SECTION_TYPE_OPTIONS } from '../../../../types/song'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { SectionDivider } from '../../shared/SectionDivider'
import { GradientBanner } from '../../shared/GradientBanner'
import { EmptyState } from '../../shared/EmptyState'

interface StructureTabProps {
  settings: SongSettings
  onUpdateSettings: <K extends keyof SongSettings>(key: K, value: SongSettings[K]) => void
  onUpdateSectionStructure: <K extends keyof SectionStructure>(index: number, key: K, value: SectionStructure[K]) => void
  onAddSectionStructure: () => void
  onRemoveSectionStructure: (index: number) => void
}

/**
 * Structure tab for song settings
 *
 * Combines section structure management, structural boxes, and target counts.
 * Maps to SongSettings.section_structure and SongSettings.structural_boxes.
 */
export const StructureTab: React.FC<StructureTabProps> = ({
  settings,
  onUpdateSettings,
  onUpdateSectionStructure,
  onAddSectionStructure,
  onRemoveSectionStructure
}) => {
  return (
    <div className="space-y-6">
      <GradientBanner
        title="Song Structure"
        description="Define the structural foundation of your song, including section order, line counts, and narrative flow."
        icon="🏗️"
        variant="info"
      />

      {/* Structural Boxes */}
      <div>
        <h3 className="text-heading font-medium text-gray-900 mb-3">Structural Boxes</h3>
        <p className="text-sm text-description text-gray-600 mb-4">
          Select which narrative boxes your song uses for storytelling.
        </p>
        <div className="space-y-2">
          {STRUCTURAL_BOX_OPTIONS.map(option => (
            <label key={option.value} className="flex items-center">
              <input
                type="checkbox"
                checked={settings.structural_boxes.includes(option.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onUpdateSettings('structural_boxes', [...settings.structural_boxes, option.value])
                  } else {
                    onUpdateSettings('structural_boxes', settings.structural_boxes.filter(box => box !== option.value))
                  }
                }}
                className="rounded border-gray-300 text-blue-600 mr-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <SectionDivider label="Section Management" />

      {/* Section Structure */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-heading font-medium text-gray-900">Section Structure</h3>
            <p className="text-sm text-description text-gray-600 mt-1">
              Define the order and type of sections in your song.
            </p>
          </div>
          <button
            onClick={onAddSectionStructure}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            title="Add Section"
            data-testid="add-section-button"
          >
            <PlusIcon className="h-4 w-4" />
            Add Section
          </button>
        </div>

        <div className="space-y-3">
          {settings.section_structure.map((section, index) => (
            <div
              key={index}
              className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50"
              data-testid={`section-structure-${index}`}
            >
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Section Label
                  </label>
                  <input
                    type="text"
                    value={section.label}
                    onChange={(e) => onUpdateSectionStructure(index, 'label', e.target.value)}
                    placeholder="Section label"
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Section Type
                  </label>
                  <select
                    value={section.type}
                    onChange={(e) => onUpdateSectionStructure(index, 'type', e.target.value as SectionType)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-900 bg-white"
                  >
                    {SECTION_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Target Lines
                  </label>
                  <input
                    type="number"
                    value={section.line_count_target || ''}
                    onChange={(e) => onUpdateSectionStructure(index, 'line_count_target', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="Lines"
                    min="1"
                    max="50"
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-900 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Target Stresses
                  </label>
                  <input
                    type="number"
                    value={section.stress_count_target || ''}
                    onChange={(e) => onUpdateSectionStructure(index, 'stress_count_target', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="Stresses"
                    min="1"
                    max="20"
                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-900 bg-white"
                  />
                </div>
              </div>
              <button
                onClick={() => onRemoveSectionStructure(index)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                title="Remove Section"
                data-testid={`remove-section-${index}`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}

          {settings.section_structure.length === 0 && (
            <EmptyState
              icon="🏗️"
              title="No Sections Yet"
              description="Define your song's structure by adding sections like verses, choruses, and bridges."
              action={{
                label: 'Add First Section',
                onClick: onAddSectionStructure
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
